import { resume } from '../../data/resume';
import ColorBars from '../layout/ColorBars';
import SectionHeading from '../layout/SectionHeading';

/**
 * Projects section: each project is rendered as a compact poster card. The
 * card header is a thin diagonal stripe band drawn from a small muted
 * palette (per index), the body holds a display title, optional
 * description, stack tokens as small bordered chips, and sticker links
 * for the repo and the live app.
 */
const PALETTES: Array<{ colors: string[]; angle: number; numAccent: string }> = [
  { colors: ['#8B2D3A', '#1A1410'], angle: 135, numAccent: 'text-accent-oxblood' },
  { colors: ['#3A6E8A', '#6B7A3F'], angle: 45, numAccent: 'text-accent-dustyBlue' },
  { colors: ['#B49533', '#C5683B'], angle: 135, numAccent: 'text-accent-mustard' },
  { colors: ['#1E2F6B', '#5C3556'], angle: 45, numAccent: 'text-accent-navy' },
];

const Projects = () => (
  <section className="bg-paper-base px-8 py-10">
    <SectionHeading kana="作品" accent="text-accent-plum">
      Projects
    </SectionHeading>

    <div className={`grid gap-6 ${resume.projects.length > 1 ? 'md:grid-cols-2' : ''}`}>
      {resume.projects.map((project, i) => {
        const p = PALETTES[i % PALETTES.length];
        const num = String(i + 1).padStart(2, '0');
        const links = [
          project.github ? { href: project.github, label: 'GitHub' } : null,
          project.app ? { href: project.app, label: 'App' } : null,
        ].filter((link): link is { href: string; label: string } => link !== null);

        return (
          <article
            key={project.name}
            className="relative h-full overflow-hidden border-[3px] border-ink bg-paper-base shadow-sleeve"
          >
            <div className="relative h-16 overflow-hidden border-b-[3px] border-ink bg-paper-warm">
              <ColorBars orientation="diagonal" colors={p.colors} stripe={28} angle={p.angle} />
              <div className="absolute inset-0 flex items-center justify-between px-4">
                <span className={`font-mono text-[0.7rem] font-bold uppercase tracking-retro ${p.numAccent}`}>
                  <span className="border-2 border-ink bg-paper-base px-2 py-0.5 shadow-sticker">
                    Nº {num}
                  </span>
                </span>
              </div>
            </div>

            <div className="p-5">
              <h3 className="font-display text-3xl font-bold uppercase leading-tight tracking-retro text-ink">
                {project.name}
              </h3>
              {project.description ? (
                <p className="mt-2 font-serif text-sm text-ink-muted">{project.description}</p>
              ) : null}

              {project.stack ? (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {project.stack.split('•').map((token) => (
                    <span
                      key={token}
                      className="border border-ink bg-paper-base px-2 py-0.5 font-mono text-[0.65rem] font-bold uppercase tracking-retro text-ink"
                    >
                      {token.trim()}
                    </span>
                  ))}
                </div>
              ) : null}

              {links.length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {links.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noreferrer"
                      className="border-2 border-ink bg-paper-base px-3 py-1 font-mono text-xs font-bold uppercase tracking-retro text-ink shadow-sticker transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-oxblood"
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  </section>
);

export default Projects;
